import Phaser from 'phaser';
import { N1_TEXTURE_KEY } from '../config';
import {
  frameName,
  tierOfLabel,
  type N1ActionId,
  type N1Manifest,
  type N1TierLabel,
} from '../assets/n1Manifest';
import type { ActionGraph } from './actionGraph';
import { LocomotionMachine, type Facing, type InputFrame, type StepResult } from './stateMachine';
import type { PlayerTuning } from './tuning';

export interface SpawnPoint {
  x: number;
  feetY: number;
  facing: Facing;
}

export interface PlayerOptions {
  /** Runtime in-betweening (ghost sprite of the next frame); default from the tuning. */
  frameBlend?: boolean;
}

type BodyFamily = 'ground' | 'run' | 'air';

export const PLAYER_DEPTH = 20;

function approach(current: number, target: number, maxDelta: number): number {
  if (maxDelta <= 0) return current;
  if (current < target) return Math.min(current + maxDelta, target);
  if (current > target) return Math.max(current - maxDelta, target);
  return current;
}

/**
 * Georgi in the world: an Arcade sprite positioned by the N1 pivot (its feet), an explicit body
 * rectangle from the tuning, and the pure locomotion machine deciding pose and motion.
 *
 * Ownership: sprite pixels are pose only. Arcade owns travel, gravity and collision; the machine
 * only sets a horizontal velocity target and the takeoff velocity. Sprite displacement is never
 * added to physics displacement (rootLift stays a gallery preview). Right facing is setFlipX;
 * the body is centred on the root so it does not move when the sprite mirrors.
 */
export class GeorgiPlayer {
  readonly sprite: Phaser.Physics.Arcade.Sprite;
  /** Draws the next frame over the sprite at the sub-frame fraction (runtime in-betweening). */
  readonly ghost: Phaser.GameObjects.Sprite;
  readonly machine: LocomotionMachine;
  private readonly tierScale: number;
  private readonly root: readonly [number, number];
  private family: BodyFamily = 'ground';
  private blendEnabled: boolean;
  private currentFrame = '';
  private lastResult: StepResult | null = null;
  private lastGrounded = false;
  /** True from (re)spawn until physics first reports the floor: Arcade has not collided yet. */
  private settling = true;

  constructor(
    scene: Phaser.Scene,
    manifest: N1Manifest,
    tierLabel: N1TierLabel,
    graph: ActionGraph,
    private readonly tuning: PlayerTuning,
    spawn: SpawnPoint,
    options: PlayerOptions = {},
  ) {
    const { scale: tierScale } = tierOfLabel(tierLabel);
    const [rootX, rootY] = manifest.tiers[tierLabel].root;
    const [pivotX, pivotY] = manifest.geometry.pivot;
    this.tierScale = tierScale;
    this.root = [rootX, rootY];
    this.blendEnabled = options.frameBlend ?? tuning.frameBlend;
    this.machine = new LocomotionMachine(graph, tuning, spawn.facing);

    const first = frameName(this.machine.pose.action, 0);
    this.sprite = scene.physics.add.sprite(spawn.x, spawn.feetY, N1_TEXTURE_KEY, first);
    this.currentFrame = first;
    this.sprite
      .setOrigin(pivotX, pivotY)
      .setScale(tuning.worldScale / tierScale)
      .setDepth(PLAYER_DEPTH)
      .setFlipX(spawn.facing === 'right');

    this.ghost = scene.add
      .sprite(spawn.x, spawn.feetY, N1_TEXTURE_KEY, first)
      .setOrigin(pivotX, pivotY)
      .setScale(tuning.worldScale / tierScale)
      .setDepth(PLAYER_DEPTH + 0.01)
      .setFlipX(spawn.facing === 'right')
      .setAlpha(0)
      .setVisible(this.blendEnabled);

    // Explicit body in tier page px relative to the page origin: root + authored rect × tier scale.
    // Arcade scales it by the sprite scale, so the world body is the authored rect × worldScale.
    const body = this.body;
    this.applyBody('ground');
    body.setCollideWorldBounds(true);
    body.setMaxVelocityY(tuning.maxFallSpeed);
    body.setAllowGravity(true);
  }

  get body(): Phaser.Physics.Arcade.Body {
    return this.sprite.body as Phaser.Physics.Arcade.Body;
  }

  /** Which authored body is active (idle/walk = ground, run, air). */
  get bodyFamily(): BodyFamily {
    return this.family;
  }

  get frameBlend(): boolean {
    return this.blendEnabled;
  }

  setFrameBlend(on: boolean): void {
    this.blendEnabled = on;
    this.ghost.setVisible(on);
    if (!on) this.ghost.setAlpha(0);
  }

  /** The body rectangle authored for a locomotion state, keyed by family. */
  private applyBody(family: BodyFamily): void {
    const b = this.tuning.bodies[family];
    const ts = this.tierScale;
    const body = this.body;
    body.setSize(b.w * ts, b.h * ts, false);
    body.setOffset(this.root[0] + b.x * ts, this.root[1] + b.y * ts);
    this.family = family;
  }

  private static familyFor(state: 'idle' | 'walk' | 'run' | 'air'): BodyFamily {
    return state === 'air' ? 'air' : state === 'run' ? 'run' : 'ground';
  }

  /**
   * World rectangle of the current frame's opaque pixels (its trimmed atlas rect, mirrored with
   * the sprite) — the visual silhouette the bodies are authored against.
   */
  get visualBounds(): { x: number; y: number; w: number; h: number } {
    const sprite = this.sprite;
    const frame = sprite.frame;
    const s = sprite.scaleX;
    const w = frame.width * s;
    const h = frame.height * s;
    const y = sprite.y - sprite.displayOriginY * s + frame.y * s;
    const x = sprite.flipX
      ? sprite.x + sprite.displayOriginX * s - (frame.x + frame.width) * s
      : sprite.x - sprite.displayOriginX * s + frame.x * s;
    return { x, y, w, h };
  }

  /** The pivot is the feet line. */
  get feetY(): number {
    return this.sprite.y;
  }

  get x(): number {
    return this.sprite.x;
  }

  get result(): StepResult | null {
    return this.lastResult;
  }

  /** What physics reported on the last update (after the machine's non-upward-velocity rule). */
  get grounded(): boolean {
    return this.lastGrounded;
  }

  get bodyRect(): { x: number; y: number; w: number; h: number } {
    const body = this.body;
    return { x: body.x, y: body.y, w: body.width, h: body.height };
  }

  /** One controller tick: read physics, decide, then apply effects, motion and pose. */
  update(
    dtMs: number,
    input: InputFrame,
    extras: { airJumps?: number; speedScale?: number; accelScale?: number } = {},
  ): StepResult {
    const body = this.body;
    let grounded = body.blocked.down || body.touching.down;
    // A spawn point stands on a platform (validated), but the first physics step has not
    // resolved that contact yet; treat it as grounded until Arcade reports the floor.
    if (this.settling) {
      if (grounded) this.settling = false;
      else grounded = body.velocity.y >= 0;
    }
    const result = this.machine.update(input, {
      grounded,
      vy: body.velocity.y,
      vx: body.velocity.x,
      dtMs,
      airJumps: extras.airJumps,
      speedScale: extras.speedScale,
    });
    const family = GeorgiPlayer.familyFor(result.state);
    if (family !== this.family) this.applyBody(family);
    this.lastGrounded = grounded && body.velocity.y >= 0;

    for (const effect of result.effects) {
      if (effect.kind === 'jump') body.setVelocityY(effect.velocityY);
      else if (effect.kind === 'flip') this.sprite.setFlipX(effect.facing === 'right');
    }

    // accelScale < 1 = slippery ground: slower to reach the target and slower to stop.
    const { targetVx, acceleration } = result.motion;
    const accel = acceleration * (extras.accelScale ?? 1);
    body.setVelocityX(approach(body.velocity.x, targetVx, (accel * dtMs) / 1000));

    const name = frameName(result.pose.action, result.pose.frame);
    if (name !== this.currentFrame) {
      this.sprite.setFrame(name);
      this.currentFrame = name;
    }
    if (this.sprite.flipX !== result.pose.flipX) this.sprite.setFlipX(result.pose.flipX);
    this.syncGhost(result.pose.action, result.pose.blend.nextFrame, result.pose.blend.fraction);
    this.lastResult = result;
    return result;
  }

  /** Keeps the ghost on the sprite, showing the next frame at the sub-frame fraction. */
  private syncGhost(action: N1ActionId, nextFrame: number, fraction: number): void {
    const ghost = this.ghost;
    if (!this.blendEnabled) return;
    const sprite = this.sprite;
    ghost.setPosition(sprite.x, sprite.y);
    ghost.setFlipX(sprite.flipX);
    ghost.setAlpha(sprite.alpha * fraction);
    const name = frameName(action, nextFrame);
    if (ghost.frame.name !== name) ghost.setFrame(name);
  }

  /** Deterministic respawn: idle loop from frame 0, no velocity, body re-synced to the pivot. */
  respawn(spawn: SpawnPoint): void {
    this.machine.reset(spawn.facing);
    const body = this.body;
    // Body.reset alone: it leaves the body at the sprite's offset-less top-left until the next
    // preUpdate re-syncs it, and re-syncing it early makes postUpdate shove the sprite by the offset.
    body.reset(spawn.x, spawn.feetY);
    body.setVelocity(0, 0);
    const name = frameName(this.machine.pose.action, 0);
    this.sprite.setFrame(name).setFlipX(spawn.facing === 'right');
    this.currentFrame = name;
    this.applyBody('ground');
    this.ghost
      .setPosition(spawn.x, spawn.feetY)
      .setFlipX(spawn.facing === 'right')
      .setAlpha(0);
    this.lastResult = null;
    this.lastGrounded = false;
    this.settling = true;
  }
}
